import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, debounce, Dialog, Grid, Menu, MenuItem, Snackbar, Typography } from "@mui/material";
import { ReactComponent as ArrowDropDownIcon } from '../../assets/PepWise2.0/arrow-down-white.svg';
import PageHeader from "components/Header/Page";
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import { usePepwiseAuthController } from "Context/contexts/AuthContext";
import { getProgramSectorList, getProjectListByProgram, getSectorProgramMapping } from "service/service";
import { ReactComponent as EditIcon } from '../../assets/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete-img.svg';
import Loader from "components/Loader/loader";
import { DataPipelineModal } from "./modal-component";
import { CustomizedDataGrid } from "components/customized_data_grid";
import RowTypography from "components/Table/RowTypography";
import { useLocation, useNavigate } from "react-router-dom";
import { checkRole } from "utils/roleUtils";
import SearchField from "components/Common/SearchField";
import PageWrapper from "components/Common/PageWraper";
import { useTheme } from '@mui/material/styles';
import { setRdgTableHeight } from "./DataPipelineHelper";
import { ipDelayedStartFormatter, ipLongRunningFormatter, FailedStatusFormatter, SuccessStatusFormatter, projectLinkFormatter, SlaStatusFormatter, slaDateTimeFormatter } from "./DataPipelineFormatter";
import SectorCard from "components/Widgets/SectorCard";
import CustomAccordion from "components/Accordion/CustomAccordion";
import { ReactComponent as SearchIcon } from '../../assets/PepWise2.0/Search-lightBlue.svg';
import { useHeader } from "Context/contexts/HeaderTabRightContentContext";

const SECTOR_MAPPING_TOOLTIPS: Record<string, string> = {
  'International Beverages':
    'EUROPE, AMESA, LATAM, and APAC Franchise Beverage Units',

  'North America':
    'PBNA, PFNA, PQGC',

  'EMEA Foods':
    'EUROPE, AMESA',

  'LATAM Foods':
    'LATAM',

  'APAC Foods':
    'APAC',

  'Global':
    'GLOBAL',

  'Corporate':
    'CORPORATE',

  'Miscellaneous':
    'SODASTREAM, RUSSIA, ETC.',

  'Multisector':
    'Programs containing projects assigned to more than one top-level sector',

  'Unassigned':
    'Programs without a resolved sector assignment'
};

const getSectorMappingTooltip = (
  sector: string
): React.ReactNode => (
  <Typography
    component="span"
    sx={{
      color: '#000000 !important',
      opacity: '1 !important',
      fontSize: '12px'
    }}
  >
    <Box
      component="span"
      sx={{
        fontWeight: 700,
        color: '#000000 !important'
      }}
    >
      {sector}:
    </Box>{' '}
    {SECTOR_MAPPING_TOOLTIPS[sector] ??
      'No legacy sector mapping information is available'}
  </Typography>
);

const NewDashboard = () => {
    const theme = useTheme();
    const colors = theme.palette;
    const { setRightContextContent } = useHeader();
        
    const location = useLocation();

    const [openModal, setOpenModal] = useState<any>({ isOpen: false });
    const [programList, setProgramList] = useState<any>([]);
    const [loading, setLoading] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);
    const [currentProgram, setCurrentProgram] = useState<any>({});
    const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
    const [projectList, setProjectList] = useState<any>([]);
    const page = 1;
    const pageSize = 100;
    const [controller] = usePepwiseAuthController();
    const { role } = controller;
    const defaultColumnNames = ['Sectors', 'Project', 'Delayed Start', 'Long Running', 'Failed', 'Success', 'SLA Status', 'SLA Time (CST)'];
    const filterData: any[] = [];
    const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
    const searchDetails: any[] = [];
    const filterInfo: any[] = [];
    const [sectorMasterList, setSectorMasterList] = useState<any[]>([]);
    const [searchProgram, setSearchProgram] = useState('');
    const [selectedProjectFilter, setSelectedProjectFilter] = useState('');
    const [toast, setToast] = useState<any>({
        open: false,
        message: ''
    });

    const [expandedSector, setExpandedSector] = useState(null);
    const handleAccordionChange = (sector: any) => (event: any, isExpanded: any) => {
        setExpandedSector(isExpanded ? sector : null);
    };
    const messagesEndRef = useRef<null | HTMLDivElement>(null);
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const isPwCView = checkRole(role, ['PwC_View']);
    const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);
    const navigate = useNavigate();
    const getColumns = () => {
        return [
            {
                key: 'Sectors',
                name: 'Sectors',
                formatter(props: any) {
                    const sectorList = props.row.Sectors ? props.row.Sectors.split(', ') : [];
                    if (sectorList?.length) {
                        return sectorList.map((sector: string, idx: number) => <RowTypography sx={{ backgroundColor: colors.table.chipBackground, color: colors.table.chipText, padding: '4px 10px', marginRight: '5px', borderRadius: '20px' }} key={sector} title={sector}>{sector}</RowTypography>);
                    } else {
                        return '--'
                    }
                },
                minWidth: 150,
                maxWidth: 250
            },
            {
                key: 'Project_Name',
                name: 'Project',
                formatter: projectLinkFormatter(currentProgram.Program_Name),
                minWidth: 200,
                maxWidth: 500
            },
            {
                key: 'IP_Delayed_Start',
                name: 'Delayed Start',
                formatter: ipDelayedStartFormatter(),
                minWidth: 100
            },
            {
                key: 'IP_Long_Running',
                name: 'Long Running',
                formatter: ipLongRunningFormatter(),
                minWidth: 100
            },
            {
                key: 'Failed',
                name: 'Failed',
                formatter: FailedStatusFormatter,
                minWidth: 100
            },
            {
                key: 'Success',
                name: 'Success',
                formatter: SuccessStatusFormatter,
                minWidth: 100
            },
            {
                key: 'SLA_Status',
                name: 'SLA Status',
                formatter: SlaStatusFormatter,
                minWidth: 150
            },
            {
                key: 'SLA_Time',
                name: 'SLA Time (CST)',
                formatter: slaDateTimeFormatter(),
                minWidth: 150
            }
        ];
    };

    const handleFilter = (e: any) => {
        console.log('FILTER', e);
    };

    const handleSearch = (e: any) => {
        console.log('FILTER', e);
    };

    const handleSorting = (sortingInfo: any) => {
        setSortingInfo(sortingInfo);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        getProjectsByProgram(currentProgram, value, pageSize);
    };

    const getToastData = (data: any) => {
        setToast({
            ...toast,
            message: data.data.message
        });
    };

    const handleClose = () => {
        setToast({
            ...toast,
            open: false
        });
    };

    const handlepageSizeChange = (e: any) => {
        getProjectsByProgram(currentProgram, page, e.target.value);
    };

    const onCreateProgramClick = () => {
        navigate(`/data-pipeline/programs-tab`);
    };

    const handleDetailsThreeDotsClick = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };
    const handleDetailsThreeDotsClose = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleProjectSLA = (programData: any, filterType: string) => {
        setSelectedProjectFilter(filterType);
        getProjectsByProgram(programData, 1, 100, filterType, getSectorId())
    }

    const onClickAddProject = (event: any, prgId: string, prgName: string) => {
        setOpenModal({
            ...openModal,
            isOpen: true,
            type: 'Project',
            action: 'CREATE',
            data: { ProgramID: prgId }
        });
        navigate(`/data-pipeline/programs-tab?programID=${prgId}&programName=${prgName}&tab=1&type=Project&isEdited=true`);
    }

    const handleEditClose = useCallback((closeType: any) => {
        setOpenModal({
            isOpen: false,
            closeType: closeType
        });
    }, []);

    const handleAction = (module: string, type: string, data: any, e: any) => {
        e.preventDefault();
        e.stopPropagation();
        handleDetailsThreeDotsClose(e);
        data = {
            ...data,
            ProgramName: data.Program_Name,
            ProgramID: data.Program_id
        };
        switch (type) {
            case 'edit':
                setOpenModal({
                    isOpen: true,
                    type: module,
                    action: 'EDIT',
                    data: data
                });
                navigate(`/data-pipeline/programs-tab?programID=${data.Program_id}&programName=${data.Program_Name}&isEdited=true`);
                break;
            case 'delete':
                setOpenModal({
                    isOpen: true,
                    type: module,
                    action: 'DELETE',
                    data: data
                });
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        // const activeId = document.getElementById('root');
        // if (window.location.pathname.includes('/data-pipeline/dataOps')) {
        //     if (activeId) {
        //         document.head.classList.add('theme-bg-blue');
        //         document.body.classList.add('theme-bg-blue');
        //     }
        // }
        localStorage.removeItem('selectedSector');
        localStorage.removeItem('filterSectors');
        localStorage.removeItem('sectorsMasterData');
        if (localStorage.getItem('filterSectors') && localStorage.getItem('sectorsMasterData')) {
            setSectorMasterList(JSON.parse(localStorage.getItem('sectorsMasterData') ?? ''));
        } else {
            getSectorData();
        }
        getProgramData(getSectorId());
    }, []);

    useEffect(() => {
        if (openModal && !openModal.isOpen && openModal.closeType && openModal.closeType == 'updated') {
            getProgramData();
            setSearchProgram('');
        }
    }, [openModal]);

    const handleSearchData = (searchText: string) => {
        setSearchProgram(searchText);
        debouncedFetchOptions(searchText);
    };
    const debouncedFetchOptions = useMemo(
        () =>
            debounce(async (searchText: string) => {
                try {
                    getProgramData(getSectorId(), searchText);
                    setProjectList([]);
                } catch (error) {
                    console.error(error);
                }
            }, 500),
        []
    );

    const getSectorId = () => {
        let sectorId;
        let selectedSctr = localStorage.getItem('selectedSector')
        if (selectedSctr && selectedSctr != 'all') {
            const sectorList = JSON.parse(localStorage.getItem('sectorsMasterData') ?? '')
            if (sectorList) {
                sectorId = sectorList.filter((s: any) => s.Sector_Name == selectedSctr)[0].Sector_Id
            }
            else if (sectorMasterList) {
                sectorId = sectorMasterList.filter((s: any) => s.Sector_Name == selectedSctr)[0].Sector_Id
            }
        }
        return sectorId;
    }

    const getSectorData = async () => {
        try {
            let sectors: any = await getProgramSectorList();
            if (sectors?.data?.sectors && Array.isArray(sectors.data.sectors)) {
                const filterSectors = sectors.data.sectors.map((sec: any) => sec.Sector_Name);
                localStorage.setItem('filterSectors', JSON.stringify(filterSectors));
                localStorage.setItem('sectorsMasterData', JSON.stringify(sectors.data.sectors));
                setSectorMasterList(sectors.data.sectors);
            }
        } catch (error) {
            console.error('Error fetching sector data:', error);
        }
    };

    const getProgramData = async (sectorId?: number, searchKeyword?: string) => {
        setLoading(true);
        try {
            let data: any = await getSectorProgramMapping(searchKeyword);
            setLoading(false);
            setProgramList(data?.data || {});
        } catch (error) {
            console.error('Error fetching program data:', error);
            setLoading(false);
            setProgramList({});
        }
    };

    const getProjectsByProgram = async (data: any, page: number, pageSize: number, filterType?: string, sectorId?: number) => {
        page = page || 1;
        setTableLoading(true);
        setCurrentProgram(data);
        let result = await getProjectListByProgram(data.Program_id, page, pageSize, filterType, {}, '', '', sortingInfo, sectorId);
        setTimeout(() => {
            setRdgTableHeight(
                `project_list${data.Program_id}`,
                result.data.projectList.length
            );
        }, 100);
        setPaginationData(result.data.page);
        setTableLoading(false);
        const projData = result.data.projectList.map((list: any) => {
            list['activeRow'] = 0;
            const selectedSectors = list.Sectors && list.Sectors.split(', ');
            list['Sectors'] = selectedSectors && selectedSectors.length >= 10 ? ['All 10 Sectors'] : list.Sectors;
            return list;
        });
        setProjectList(projData);
        setTimeout(() => {
            scrollToBottom();
        }, 500);
    };
    // Type for a program object (customize fields as needed)
    type Program = {
        [key: string]: any; // or define specific fields if you want
    };

    // Type for the main object: keys are sector names, values are arrays of programs
    type ProgramList = {
        [sector: string]: Program[];
    };

    const handleDropdownClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleDropdownClose = () => {
        setAnchorEl(null);
    };

    // Helper function to clear session storage
    const clearSessionData = () => {
        sessionStorage.removeItem('executiveTemplateTab');
        sessionStorage.removeItem('executiveTemplateDashboard');
        sessionStorage.removeItem('executiveTemplateWorkflow');
        sessionStorage.removeItem('executiveTemplateHops');
        sessionStorage.removeItem('executiveTemplateSla'); 
    };

    const handleExecutiveTemplate = () => {
        handleDropdownClose();
        clearSessionData();
        navigate(`/data-pipeline/executiveTemplate`);
    };

    const getProjectFilterColor = (label: string) => {
        const normalized = label.trim().toLowerCase();

        if (normalized === 'sla missed' || normalized === 'missed') return colors.widget.sla.missed.bg;
        if (normalized === 'sla met' || normalized === 'met') return colors.widget.sla.met.bg;
        if (normalized === 'in progress') return colors.widget.sla.progress.bg;
        if (normalized === 'scheduled') return colors.widget.sla.scheduled.bg;
        if (normalized === 'at risk') return colors.widget.sla.others.bg;

        return colors.widget.sla.others.bg;
    };

    const projectFilterColor = getProjectFilterColor(selectedProjectFilter);

    useEffect(() => {
        setRightContextContent(
        <Box display="flex" alignItems="center" gap={2}>
            <SearchField
                value={searchProgram}
                type="programs"
                onChange={handleSearchData}
                onSearch={() => {}}
                onClear={() => handleSearchData('')}
                searchIcon={<SearchIcon />}
            />
        </Box>,
        );

        return () => {
        setRightContextContent(null);
        };
    }, [searchProgram, handleSearchData]);

    return (
        <PageWrapper>
        <Box
            className={"main-bordered-container"}
            sx={{
                backgroundColor: `${colors.backgroundLight.primary} !important`,
                minHeight: '100vh'
            }}
        >
            <PageHeader title="Programs Dashboard" icon={DashboardIcon} lastSync={'Today'} isDataRefresh={true}>

                {isAllowed && (
                    <>
                        <Button
                            variant="pepwiseSecondary"
                            onClick={handleDropdownClick}
                            sx={{ height: '43px' }}
                            endIcon={<ArrowDropDownIcon />}
                        >
                            CREATE PROGRAM
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleDropdownClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            MenuListProps={{
                                sx: {
                                    backgroundColor: `${colors.buttons.primary.solidBg} !important`,
                                    }
                            }}
                            slotProps={{
                                paper: {
                                    sx: {
                                    borderRadius: '4px',
                                    color: `${colors.buttons.primary.solidText} !important`,
                                    boxShadow: '0px 4px 16px rgba(0,0,0,0.3)',
                                    mt: 0,
                                }
                            }
                        }}
                        >
                        <MenuItem onClick={onCreateProgramClick}
                            sx={{
                                color: `${colors.buttons.primary.solidText} !important`,
                                borderRadius: '4px',
                                textTransform: 'uppercase',
                                fontFamily: 'Montserrat',
                                '&:hover': {
                                    backgroundColor: `${colors.buttons.primary.borderedHover} !important`,
                                    color: `${colors.buttons.primary.solidText} !important`,
                                }
                            }}
                            ><Button variant="pepwiseButtonText">CREATE PROGRAM</Button></MenuItem>
                            <MenuItem onClick={handleExecutiveTemplate}
                            sx={{
                                color: `${colors.buttons.primary.solidText} !important`,
                                borderRadius: '4px',
                                textTransform: 'uppercase',
                                fontFamily: 'Montserrat',
                                '&:hover': {
                                    backgroundColor: `${colors.buttons.primary.borderedHover} !important`,
                                    color: `${colors.buttons.primary.solidText} !important`,
                                }
                            }}
                            ><Button variant="pepwiseButtonText">EXECUTIVE TEMPLATE</Button></MenuItem>
                        </Menu>
                    </>
                )}
            </PageHeader>

            <Box p={4} mt={3} mb={3}>
                {!loading ? (
                    <Grid container spacing={2}>
                        {(programList && typeof programList === 'object' && !Array.isArray(programList) ? Object.entries(programList) : []).map(([sector, programs], sectorIndex) => {
                            const panelId = `sector-panel-${sectorIndex}`;

                            return (
                                <Box key={sector} sx={{ width: '100%', mt: 2 }}>
                                    <CustomAccordion
                                        id={panelId}
                                        title={sector}
                                        titleTooltip={getSectorMappingTooltip(sector)}
                                        expanded={expandedSector === sector}
                                        onChange={handleAccordionChange(sector)}
                                        isActive
                                        statusLabel={sector}
                                        renderDetails={
                                            expandedSector === sector ? (
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        p: 2,
                                                        boxSizing: 'border-box',
                                                        backgroundColor: colors.dataPipeline.accordionWidget.background.page,
                                                    }}
                                                >
                                                    <Grid container spacing={2}>
                                                        {Array.isArray(programs) && programs?.length === 0 ? (
                                                            <Grid item xs={12}>
                                                                <Typography color="textSecondary">No Programs</Typography>
                                                            </Grid>
                                                        ) : (
                                                            Array.isArray(programs) && programs?.map((program: any) => {
                                                                const statusCount =
                                                                    program.SLA_Missed_Count +
                                                                    program.SLA_Met_Count +
                                                                    (program?.Scheduled_Count ? program.Scheduled_Count : 0) +
                                                                    (program?.In_Progress_Count ? program.In_Progress_Count : 0);

                                                                return (
                                                                    <Grid key={program.Program_id} container item xs={12} sm={6} md={4}>
                                                                        <SectorCard
                                                                            title={program.Program_Name}
                                                                            sectorId={program.Program_id}
                                                                            isEditEnabled={isAllowed}
                                                                            totalLabel="TOTAL PROJECTS"
                                                                            totalCount={program.ProjectCount}
                                                                            sectionLabel="Projects SLAs"
                                                                            actionItems={[
                                                                                {
                                                                                    label: 'SLA Missed',
                                                                                    count: program.SLA_Missed_Count,
                                                                                    onBtnClick: (e) => handleProjectSLA(program, 'SLA Missed')
                                                                                },
                                                                                {
                                                                                    label: 'SLA Met',
                                                                                    count: program.SLA_Met_Count,
                                                                                    onBtnClick: (e) => handleProjectSLA(program, 'SLA Met')
                                                                                },
                                                                                {
                                                                                    label: 'In Progress',
                                                                                    count: program.In_Progress_Count || 0,
                                                                                    onBtnClick: (e) => handleProjectSLA(program, 'In Progress')
                                                                                },
                                                                                {
                                                                                    label: 'Scheduled',
                                                                                    count: program.Scheduled_Count || 0,
                                                                                    onBtnClick: (e) => handleProjectSLA(program, 'Scheduled')
                                                                                }
                                                                            ]}
                                                                            emptyStateText={statusCount === 0 ? 'No Data' : ''}
                                                                            addButtonLabel="ADD PROJECT"
                                                                            infoTooltip={{
                                                                                dateValues: [
                                                                                    { label: 'Date Created', value: program?.Created_dt ?? '' },
                                                                                    { label: 'Last Updated', value: program?.Updated_dt ?? '' }
                                                                                ]
                                                                            }}
                                                                            infoOptionMenu={{
                                                                                onClick: handleDetailsThreeDotsClick,
                                                                                // onClose: handleDetailsThreeDotsClose,
                                                                                menuItems:[
                                                                                    ...(!isPwCView ? [
                                                                                        {
                                                                                            key: 'edit',
                                                                                            label: 'Edit',
                                                                                            icon: <EditIcon />,
                                                                                            onMenuClick: (e: any) => handleAction('Program', 'edit', program, e)
                                                                                        },
                                                                                        {
                                                                                            key: 'delete',
                                                                                            label: 'Delete',
                                                                                            icon: <DeleteIcon />,
                                                                                            onMenuClick: (e: any) => handleAction('Program', 'delete', program, e)
                                                                                        }
                                                                                    ] : []),
                                                                                ],
                                                                                sx: { width: '20px', height: '20px' }
                                                                            }}
                                                                            onClickAddItem={(e) => onClickAddProject(e, program.Program_id, program.Program_Name)}
                                                                        />
                                                                    </Grid>
                                                                );
                                                            })
                                                        )}
                                                    </Grid>
                                                </Box>
                                            ) : null
                                        }
                                        colors={colors.dataPipeline.accordionWidget}
                                    />
                                </Box>
                            );
                        })}

                    </Grid>
                ) : (
                    <Loader />
                )
                }
            </Box>

            {projectList.length > 0 && (
                <Box p={3} sx={{ position: 'relative' }}>
                    <Box sx={{ position: 'absolute', top: 0, display: 'flex', alignItems: 'center', gap: 1 }} ref={messagesEndRef}>
                        <Typography variant="overlayWidgetTitle" sx={{ color: 'textLight.main' }}>{currentProgram.Program_Name}</Typography>
                        {selectedProjectFilter && (
                            <Typography variant="body2" sx={{ color: projectFilterColor, fontWeight: 500 }}>
                                {selectedProjectFilter && (
                                    <Typography variant="body2" sx={{ color: projectFilterColor, fontWeight: 500 }}>
                                        {paginationData.totalElements} {selectedProjectFilter} {paginationData.totalElements === 1 ? 'Project' : 'Projects'}
                                    </Typography>
                                )}
                            </Typography>
                        )}
                    </Box>


                    <CustomizedDataGrid
                        tableColumns={getColumns()}
                        tableData={projectList}
                        tableType="new-dashboard"
                        defaultColumnNames={defaultColumnNames}
                        onSorting={handleSorting}
                        currSorting={sortingInfo}
                        filterDataOptions={filterData}
                        searchDataValues={searchDetails}
                        onFilterCheck={handleFilter}
                        currFilter={filterInfo}
                        onFilterSearch={handleSearch}
                        page={page}
                        pageSize={pageSize}
                        paginationData={paginationData}
                        listLength={projectList.length}
                        handlepageSizeChange={handlepageSizeChange}
                        handlePageChange={handlePageChange}
                        isPrevNextPagination
                        isFetching={tableLoading}
                        showExport={false}
                        isDateRangeFilter={false}
                        isDisabled={false}
                        isShowMoreButton={false}
                    />
                </Box>
            )}
            {tableLoading && <Loader />}

            <Dialog open={openModal.isOpen} onClose={handleEditClose} fullWidth={true} maxWidth="md">
                <DataPipelineModal handleEditClose={handleEditClose} programData={openModal}
                    getToastData={getToastData}
                />
            </Dialog>
            <Snackbar
                open={toast && openModal.message}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                autoHideDuration={6000}
                onClick={handleClose}
                message={openModal.message}
            />
        </Box>
        </PageWrapper>
    );
}

export default NewDashboard;